/* eslint-disable prettier/prettier */
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Input } from '../components/ui/Input';
import { Button } from '../components/ui/Button';
import { NotFoundPage } from './NotFoundPage';
import { UserService } from '../services/user.service';
import { Card } from 'antd';
import Illustration from '../assets/man-working.png';
import { CustomSelect } from '../components/ui/Select';
import { CheckCircle, XCircle } from 'lucide-react';
import { getCountries, getCountryCallingCode } from 'libphonenumber-js';
import metadata from 'libphonenumber-js/metadata.min.json';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import { useAuth } from '../context/AuthContext';

const countryOptions = getCountries().map((country) => {
  const dialCode = `+${getCountryCallingCode(country)}`;
  return {
    label: `${dialCode} (${country})`,
    value: dialCode,
  };
});

export const AcceptInvitationPage = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    token: token || '',
    firstName: '',
    lastName: '',
    username: '',
    phoneNumber: '',
    countryCode: '+33',
    password: '',
    confirmPassword: '',
  });

  const [errors, setErrors] = useState<any>({});
  const [isValidToken, setIsValidToken] = useState<boolean | null>(null);
  const [errorData, setErrorData] = useState<any>(null);
  const [success, setSuccess] = useState(false);
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);

  const { login } = useAuth();

  const handleChange = (key: string, value: string) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev: any) => ({ ...prev, [key]: '' }));
  };

  const validate = () => {
    const newErrors: any = {};
    if (!form.firstName) newErrors.firstName = 'First name is required';
    if (!form.lastName) newErrors.lastName = 'Last name is required';
    if (!form.username) newErrors.username = 'Username is required';
    if (!form.countryCode) newErrors.countryCode = 'Country code is required';
    if (!form.password) {
      newErrors.password = 'Password is required';
    } else {
      if (form.password.length < 6)
        newErrors.password = 'Password must be at least 6 characters long';
      else if (!/[A-Z]/.test(form.password))
        newErrors.password = 'Password must contain at least one capital letter';
      else if (!/[0-9]/.test(form.password))
        newErrors.password = 'Password must contain at least one number';
    }
    if (form.confirmPassword !== form.password)
      newErrors.confirmPassword = 'Passwords do not match';
    if (!form.phoneNumber) {
      newErrors.phoneNumber = 'Phone number is required';
    } else {
      const fullNumber = `${form.countryCode}${form.phoneNumber}`;
      const parsed = parsePhoneNumberFromString(fullNumber);
      if (!parsed || !parsed.isValid()) {
        newErrors.phoneNumber = 'Invalid phone number';
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
  e.preventDefault();

  if (!validate()) return;

  setLoading(true); 

  try {
    const usernameToCheck = form.username.trim().toLowerCase();
    const { exists } = await UserService.checkUsernameAvailability(usernameToCheck);
    if (exists) {
      setErrors((prev: any) => ({
        ...prev,
        username: 'Username is already taken. Please choose another one.',
      }));
      setLoading(false); 
      return;
    }

    await UserService.acceptInvitation({
      ...form,
      username: usernameToCheck,
    });

    setSuccess(true);
    sessionStorage.setItem('invitation-accepted', 'true');
    await login(email, form.password);
  } catch (err: any) {
    const raw = err?.response?.data?.message;
    const normalized = typeof raw === 'string' ? raw : raw?.message || 'Account creation failed.';
    setErrorData(normalized);
  } finally {
    setLoading(false); 
  }
};


  useEffect(() => {
    const validateToken = async () => {
      try {
        const data = await UserService.verifyInvitationToken(token!);
        setIsValidToken(true);
        setEmail(data?.email);
      } catch (err: any) {
        setIsValidToken(false);
        const raw = err?.response?.data?.message;
        const normalized =
          typeof raw === 'string' ? raw : raw?.message || 'Invalid or expired invitation link.';
        setErrorData(normalized);
      }
    };
    if (token) validateToken();
  }, [token]);

  const passwordRules = [
    {
      label: 'At least 6 characters',
      valid: form.password.length >= 6,
    },
    {
      label: 'Contains a capital letter',
      valid: /[A-Z]/.test(form.password),
    },
    {
      label: 'Contains a number',
      valid: /[0-9]/.test(form.password),
    },
  ];

  if (isValidToken === false) {
    const message = typeof errorData === 'string' ? errorData : 'Invalid invitation link.';
    let customMessage = message;
    let buttonText = 'Go back';
    let buttonAction = () => navigate('/');

    if (message.includes('already been used')) {
      customMessage = 'This invitation has already been accepted.';
      buttonText = 'Login';
      buttonAction = () => navigate('/login');
    } else if (message.includes('expired') || message.includes('Invalid')) {
      customMessage = 'This invitation link is invalid or has expired.';
      buttonText = 'Login';
      buttonAction = () => navigate('/login');
    }

    return (
      <NotFoundPage message={customMessage} buttonLabel={buttonText} buttonAction={buttonAction} />
    );
  }

  if (isValidToken === null) {
    return null;
  }

  return (
    <div className="relative flex items-center justify-center min-h-screen overflow-hidden bg-light">
      <Card className="px-4 py-10 flex flex-col items-center shadow-lg bg-white relative min-h-[650px] w-full max-w-xl">
        <h2 className="mb-6 text-2xl font-bold text-center text-primary">Complete Your Account</h2>
        <img src={Illustration} alt="Illustration" className="w-32 h-32 mx-auto mb-6" />

        {errorData && typeof errorData === 'string' && (
          <p className="mb-4 text-center text-red-500">{errorData}</p>
        )}

        <form className="w-full space-y-4" onSubmit={handleSubmit}>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Input
                placeholder="First Name"
                value={form.firstName}
                onChange={(e) => handleChange('firstName', e.target.value)}
                error={!!errors.firstName}
              />
              {errors.firstName && <p className="text-sm text-red-500">{errors.firstName}</p>}
            </div>
            <div>
              <Input
                placeholder="Last Name"
                value={form.lastName}
                onChange={(e) => handleChange('lastName', e.target.value)}
                error={!!errors.lastName}
              />
              {errors.lastName && <p className="text-sm text-red-500">{errors.lastName}</p>}
            </div>
          </div>

          <div>
            <Input
              placeholder="Username"
              value={form.username}
              onChange={(e) => handleChange('username', e.target.value)}
              error={!!errors.username}
            />
            {errors.username && <p className="text-sm text-red-500">{errors.username}</p>}
          </div>

          <div className="grid grid-cols-3 gap-4">
            <CustomSelect
              data={countryOptions}
              value={form.countryCode}
              onChange={(val) => handleChange('countryCode', val)}
              error={!!errors.countryCode}
              errorMessage={errors.countryCode}
            />
            <div className="col-span-2">
              <Input
                placeholder="Phone Number"
                value={form.phoneNumber}
                onChange={(e) => handleChange('phoneNumber', e.target.value)}
                error={!!errors.phoneNumber}
              />
              {errors.phoneNumber && <p className="text-sm text-red-500">{errors.phoneNumber}</p>}
            </div>
          </div>

          <div>
            <Input
              placeholder="Password"
              type="password"
              value={form.password}
              onChange={(e) => handleChange('password', e.target.value)}
              error={!!errors.password}
            />
            {errors.password && <p className="text-sm text-red-500">{errors.password}</p>}
            {form.password !== '' && (
              <ul className="mt-2 space-y-1 text-sm">
                {passwordRules.map((rule, index) => (
                  <li
                    key={index}
                    className={`flex items-center gap-2 ${
                      rule.valid ? 'text-green-600' : 'text-red-500'
                    }`}
                  >
                    {rule.valid ? (
                      <CheckCircle className="w-4 h-4" />
                    ) : (
                      <XCircle className="w-4 h-4" />
                    )}
                    {rule.label}
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div>
            <Input
              placeholder="Confirm Password"
              type="password"
              value={form.confirmPassword}
              onChange={(e) => handleChange('confirmPassword', e.target.value)}
              error={!!errors.confirmPassword}
            />
            {errors.confirmPassword && (
              <p className="text-sm text-red-500">{errors.confirmPassword}</p>
            )}
          </div>

          <div className="pt-3">
            <Button type="submit" className="w-full" disabled={loading}>
            {loading ? 'Creating...' : 'Create Account'}
          </Button>

          </div>
        </form>
      </Card>
    </div>
  );
};
